import React, { useState, useEffect } from 'react';
import CountrySelector from './CountrySelector';
import CitySelector from './CitySelector';
import DaysSelector from './DaysSelector';
import BuildTrip from './BuildTrip';
import TripItinerary from './TripItinerary';
import BuildingTripLoader from './BuildingTripLoader';
import TripModeSelector, { TripBuildMode } from './TripModeSelector';
import TextTripBuilder from './TextTripBuilder';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Button from '@mui/material/Button';
import Slider from '@mui/material/Slider';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import Switch from '@mui/material/Switch';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Alert from '@mui/material/Alert';
import TuneIcon from '@mui/icons-material/Tune';
import * as api from '../api';
import { TripBuildError } from '../api';
import * as analytics from '../analytics';
import { getTravelModeIcon, getCuisineIcon, getActivityIcon } from '../utils/iconMapping';

const steps = [
  'Select Country',
  'Select Cities',
  'Select Days',
  'Trip Preferences',
  'Build Trip',
];

function TripWizard({ onTripBuilt }) {
  // Mode selection: null = not selected, 'wizard' = step-by-step, 'text' = describe trip
  const [buildMode, setBuildMode] = useState<TripBuildMode | null>(null);

  const [country, setCountry] = useState(null);
  const [cities, setCities] = useState([]);
  const [preserveCityOrder, setPreserveCityOrder] = useState(false);
  const [days, setDays] = useState(1);
  const [tripResult, setTripResult] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [buildingTrip, setBuildingTrip] = useState(false);
  const [buildError, setBuildError] = useState<string | null>(null);

  // Optional preferences
  const [budget, setBudget] = useState(100);
  const [totalTravelers, setTotalTravelers] = useState(1);
  const [originAirport, setOriginAirport] = useState('');
  const [departureAirport, setDepartureAirport] = useState('');
  const [travelMode, setTravelMode] = useState('');
  const [cuisineTypes, setCuisineTypes] = useState([]);
  const [activityTypes, setActivityTypes] = useState([]);

  // Lookup data
  const [airports, setAirports] = useState([]);
  const [preferencesLookup, setPreferencesLookup] = useState({
    activity_types: [],
    cuisine_types: [],
    travel_modes: [],
  });

  // Fetch airports and preferences lookup on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [airportsData, prefsData] = await Promise.all([
          api.fetchAirports(),
          api.fetchTripPreferences()
        ]);
        setAirports(airportsData);
        setPreferencesLookup(prefsData);
      } catch (err) {
        console.error('Error fetching lookup data:', err);
      }
    };
    fetchData();
  }, []);

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);
  const handleReset = () => {
    setBuildMode(null);
    setCountry(null);
    setCities([]);
    setPreserveCityOrder(false);
    setDays(1);
    setTripResult(null);
    setActiveStep(0);
    setBudget(100);
    setTotalTravelers(1);
    setOriginAirport('');
    setDepartureAirport('');
    setTravelMode('');
    setCuisineTypes([]);
    setActivityTypes([]);
  };

  const handleModeSelect = (mode: TripBuildMode) => {
    analytics.trackTripModeSelected(mode);
    setBuildMode(mode);
  };

  const handleBackToModeSelect = () => {
    setBuildMode(null);
  };

  const handleBuildTrip = async () => {
    setBuildingTrip(true);
    setBuildError(null); // Clear any previous error
    const startTime = Date.now();

    // Track build started
    analytics.trackBuildTripStarted({
      countryId: country ? Number(country) : 0,
      cityCount: cities.length,
      days: days,
    });

    // Track preferences if any were set
    if (budget > 100 || totalTravelers > 1 || travelMode || cuisineTypes.length > 0 || activityTypes.length > 0) {
      analytics.trackPreferencesSet({
        budget: budget > 100 ? budget : undefined,
        travelers: totalTravelers > 1 ? totalTravelers : undefined,
        travelMode: travelMode || undefined,
        cuisineTypes: cuisineTypes.length > 0 ? cuisineTypes : undefined,
        activityTypes: activityTypes.length > 0 ? activityTypes : undefined,
      });
    }

    try {
      const data = await api.buildTrip({
        country_id: country ? Number(country) : null,
        city_ids: cities.length > 0 ? cities.map(Number) : [],
        duration_in_days: days || null,
        budget: budget || null,
        total_travelers: totalTravelers || null,
        origin_airport: originAirport || undefined,
        departure_airport: departureAirport || undefined,
        travel_mode: travelMode || undefined,
        cuisine_type: cuisineTypes.length > 0 ? cuisineTypes : [],
        activity_type: activityTypes.length > 0 ? activityTypes : [],
        preserve_city_order: preserveCityOrder,
      });

      // Track build completed
      const duration = Date.now() - startTime;
      analytics.trackBuildTripCompleted(data.trip?.id || 0, duration);

      setTripResult(data);

      // Call the parent callback if provided
      if (onTripBuilt) {
        onTripBuilt(data);
      } else {
        setActiveStep(5); // Fallback to internal state
      }
    } catch (err) {
      console.error('Error building trip:', err);
      analytics.trackBuildTripFailed(err instanceof Error ? err.message : 'Unknown error');

      // Show user-friendly error message
      if (err instanceof TripBuildError) {
        setBuildError(err.message);
      } else {
        setBuildError('Failed to build trip. Please try again.');
      }
    } finally {
      setBuildingTrip(false);
    }
  };

  // Mode selection screen
  if (buildMode === null) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <TripModeSelector onSelectMode={handleModeSelect} />
      </Container>
    );
  }

  // Text-based trip builder
  if (buildMode === 'text') {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <TextTripBuilder
          onBack={handleBackToModeSelect}
          onTripBuilt={(data) => {
            setTripResult(data);
            if (onTripBuilt) {
              onTripBuilt(data);
            }
          }}
        />
      </Container>
    );
  }

  // Wizard mode (step-by-step)
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h5" align="center" gutterBottom sx={{ mb: 4 }}>
        Plan Your Custom Trip
      </Typography>
      <Stepper
        activeStep={activeStep}
        alternativeLabel
        sx={{
          mb: 4,
          '& .MuiStepLabel-label': {
            fontSize: { xs: '0.7rem', sm: '0.875rem' },
          }
        }}
      >
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      {activeStep === 0 && (
        <Box mb={3}>
          <CountrySelector
            selectedCountry={country}
            onSelectCountry={(val) => {
              setCountry(val);
              setCities([]);
            }}
          />
          <Box mt={2} display="flex" justifyContent="space-between">
            <Button onClick={handleBackToModeSelect}>
              Back
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleNext}
              disabled={!country}
            >
              Next
            </Button>
          </Box>
        </Box>
      )}
      {activeStep === 1 && (
        <Box mb={3}>
          <CitySelector
            country={country}
            selectedCities={cities}
            onSelectCities={setCities}
          />
          <Box mt={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={preserveCityOrder}
                  onChange={(_, checked) => setPreserveCityOrder(checked)}
                  color="primary"
                  disabled={cities.length < 2}
                />
              }
              label={
                <Typography variant="body2" color={cities.length < 2 ? "text.disabled" : "text.secondary"}>
                  Preserve city order
                </Typography>
              }
            />
          </Box>
          <Box mt={2} display="flex" justifyContent="space-between">
            <Button onClick={handleBack}>Back</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleNext}
              disabled={cities.length === 0}
            >
              Next
            </Button>
          </Box>
        </Box>
      )}
      {activeStep === 2 && (
        <Box mb={3}>
          <DaysSelector days={days} onChangeDays={setDays} />
          <Box mt={2} display="flex" justifyContent="space-between">
            <Button onClick={handleBack}>Back</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleNext}
              disabled={days < 1}
            >
              Next
            </Button>
          </Box>
        </Box>
      )}
      {activeStep === 3 && (
        <Box mb={3}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <TuneIcon color="primary" />
            <Typography variant="h6">Trip Preferences (Optional)</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Customize your trip with these optional preferences
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Budget */}
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Budget: ${budget.toLocaleString()}
              </Typography>
              <Slider
                value={budget}
                onChange={(e, newValue) => setBudget(newValue as number)}
                min={100}
                max={50000}
                step={100}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => `$${value.toLocaleString()}`}
                marks={[
                  { value: 100, label: '$100' },
                  { value: 25000, label: '$25K' },
                  { value: 50000, label: '$50K' }
                ]}
              />
            </Box>

            {/* Number of Travelers */}
            <FormControl fullWidth>
              <InputLabel>Number of Travelers</InputLabel>
              <Select
                value={totalTravelers}
                label="Number of Travelers"
                onChange={(e) => setTotalTravelers(e.target.value as number)}
              >
                {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                  <MenuItem key={num} value={num}>{num}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Origin Airport */}
            <Autocomplete
              options={airports}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                return option.name && option.id ? `${option.name} (${option.id})` : (option.id || option.name || '');
              }}
              value={airports.find(a => a.id === originAirport) || null}
              onChange={(e, newValue) => {
                const airportCode = typeof newValue === 'string' ? newValue : (newValue?.id || '');
                setOriginAirport(airportCode);
              }}
              renderInput={(params) => (
                <TextField {...params} label="Origin Airport" placeholder="e.g., JFK" />
              )}
            />

            {/* Departure Airport */}
            <Autocomplete
              options={airports}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                return option.name && option.id ? `${option.name} (${option.id})` : (option.id || option.name || '');
              }}
              value={airports.find(a => a.id === departureAirport) || null}
              onChange={(e, newValue) => {
                const airportCode = typeof newValue === 'string' ? newValue : (newValue?.id || '');
                setDepartureAirport(airportCode);
              }}
              renderInput={(params) => (
                <TextField {...params} label="Return Airport" placeholder="e.g., LIS" />
              )}
            />

            {/* Travel Mode */}
            {preferencesLookup.travel_modes && preferencesLookup.travel_modes.length > 0 && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Preferred Travel Mode
                </Typography>
                <ToggleButtonGroup
                  value={travelMode}
                  exclusive
                  onChange={(e, newValue) => {
                    if (newValue !== null) setTravelMode(newValue);
                  }}
                  fullWidth
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    '& .MuiToggleButton-root': {
                      flex: { xs: '1 1 45%', sm: 1 },
                      textTransform: 'capitalize',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.5,
                      py: 1.5,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      '&.Mui-selected': {
                        backgroundColor: 'primary.main',
                        color: 'white',
                        borderColor: 'primary.main',
                        '&:hover': {
                          backgroundColor: 'primary.dark',
                        }
                      }
                    }
                  }}
                >
                  {preferencesLookup.travel_modes.map((mode) => (
                    <ToggleButton key={mode} value={mode}>
                      {getTravelModeIcon(mode)}
                      <Typography variant="caption">{mode}</Typography>
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>
            )}

            {/* Cuisine Types */}
            {preferencesLookup.cuisine_types && preferencesLookup.cuisine_types.length > 0 && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Cuisine Preferences
                </Typography>
                <FormGroup sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1 }}>
                  {preferencesLookup.cuisine_types.map((cuisine) => (
                    <FormControlLabel
                      key={cuisine}
                      control={
                        <Switch
                          checked={cuisineTypes.includes(cuisine)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setCuisineTypes([...cuisineTypes, cuisine]);
                            } else {
                              setCuisineTypes(cuisineTypes.filter(c => c !== cuisine));
                            }
                          }}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getCuisineIcon(cuisine)}
                          <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                            {cuisine}
                          </Typography>
                        </Box>
                      }
                      sx={{ m: 0 }}
                    />
                  ))}
                </FormGroup>
              </Box>
            )}

            {/* Activity Types */}
            {preferencesLookup.activity_types && preferencesLookup.activity_types.length > 0 && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Activity Preferences
                </Typography>
                <FormGroup sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1 }}>
                  {preferencesLookup.activity_types.map((activity) => (
                    <FormControlLabel
                      key={activity}
                      control={
                        <Switch
                          checked={activityTypes.includes(activity)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setActivityTypes([...activityTypes, activity]);
                            } else {
                              setActivityTypes(activityTypes.filter(a => a !== activity));
                            }
                          }}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getActivityIcon(activity)}
                          <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                            {activity}
                          </Typography>
                        </Box>
                      }
                      sx={{ m: 0 }}
                    />
                  ))}
                </FormGroup>
              </Box>
            )}
          </Box>

          {buildError && (
            <Alert severity="error" sx={{ mt: 3 }} onClose={() => setBuildError(null)}>
              {buildError}
            </Alert>
          )}

          <Box mt={3} display="flex" justifyContent="space-between">
            <Button onClick={handleBack}>Back</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleBuildTrip}
              disabled={buildingTrip}
            >
              {buildingTrip ? 'Building Trip...' : 'Build Trip'}
            </Button>
          </Box>
        </Box>
      )}
      {activeStep === 4 && (
        <Box mb={3}>
          <BuildTrip
            country={country}
            cities={cities}
            days={days}
            onBack={handleBack}
            onReset={handleReset}
            onTripBuilt={(result) => {
              setTripResult(result);
              handleNext();
            }}
            preserveCityOrder={preserveCityOrder}
          />
        </Box>
      )}
      {activeStep === 5 && (
        <Box mb={3}>
          <TripItinerary
            tripData={tripResult}
            onBack={handleBack}
            onReset={handleReset}
            isAuthenticated={false}
            isSavedTrip={false}
            onTripSaved={undefined}
          />
        </Box>
      )}
      <BuildingTripLoader open={buildingTrip} />
    </Container>
  );
}

export default TripWizard; 